import { createContext, useContext, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface TabDefinition {
  id: string;
  label: string;
  badge?: ReactNode;
}

/**
 * Shared identity contract for one tab group.
 *
 * `TabsGroup` owns a single instance namespace (React `useId`, stable across
 * rerenders and hydration). `Tabs` and `TabPanel` both derive their DOM ids from
 * it through `tabDomId` / `panelDomId`, so the `aria-controls` / `aria-labelledby`
 * pairs stay exact when several groups (or nested groups) reuse the same logical
 * tab ids. Domain ids, URL state and routing are never touched.
 */
const TabsGroupContext = createContext<string | null>(null);

const SAFE_ID_CHARS = /[^A-Za-z0-9_-]/g;

/** Injective, whitespace-free encoding of a logical id for use inside a DOM id. */
function encodeLogicalId(id: string): string {
  return id.replace(SAFE_ID_CHARS, (char) => `.${char.charCodeAt(0).toString(16)}.`);
}

export function tabDomId(namespace: string, tabId: string): string {
  return `${namespace}-tab-${encodeLogicalId(tabId)}`;
}

export function panelDomId(namespace: string, tabId: string): string {
  return `${namespace}-panel-${encodeLogicalId(tabId)}`;
}

export interface TabsGroupProps {
  /**
   * Optional explicit namespace; defaults to a stable generated one. It is encoded like a logical
   * id, so whitespace or other unsafe characters can never split an IDREF list.
   */
  id?: string;
  children: ReactNode;
}

/** Wrap one `Tabs` and its `TabPanel`s. Nest a new group for each inner tab set. */
export function TabsGroup({ id, children }: TabsGroupProps) {
  const generated = useId();
  const namespace = id === undefined ? generated : encodeLogicalId(id);
  return <TabsGroupContext.Provider value={namespace}>{children}</TabsGroupContext.Provider>;
}

export interface TabsProps {
  tabs: TabDefinition[];
  activeId: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  className?: string;
}

export function Tabs({ tabs, activeId, onChange, ariaLabel, className = '' }: TabsProps) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  // Inside a `TabsGroup` the ids are shared with the panels. A standalone `Tabs` has no panels to
  // point at, so it still gets unique tab ids but deliberately omits `aria-controls`.
  const groupNamespace = useContext(TabsGroupContext);
  const standaloneNamespace = useId();
  const namespace = groupNamespace ?? standaloneNamespace;
  const hasActiveTab = tabs.some((tab) => tab.id === activeId);
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (tabs.length === 0) return;
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    onChange(tabs[next].id);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`flex gap-1 overflow-x-auto border-b border-slate-700/70 px-1 [scrollbar-width:none] ${className}`.trim()}
    >
      {tabs.map((tab, index) => {
        const active = tab.id === activeId;
        // Roving tabindex: when `activeId` is missing/invalid, the first tab stays the
        // keyboard entry point. Nothing is reported as selected and `onChange` is not
        // called, so the consumer remains the single owner of selection state.
        const tabStop = hasActiveTab ? active : index === 0;
        return (
          <button
            key={tab.id}
            ref={(node) => { refs.current[index] = node; }}
            id={tabDomId(namespace, tab.id)}
            role="tab"
            type="button"
            aria-selected={active}
            aria-controls={groupNamespace === null ? undefined : panelDomId(groupNamespace, tab.id)}
            tabIndex={tabStop ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => move(event, index)}
            className={`relative flex min-h-11 flex-none items-center gap-2 px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-500 ${
              active ? 'text-white after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:bg-brand-500' : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab.label}
            {tab.badge}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  id: string;
  active: boolean;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ id, active, children, className = '' }: TabPanelProps) {
  const namespace = useContext(TabsGroupContext);
  if (namespace === null) {
    throw new Error('TabPanel must be rendered inside a TabsGroup so it shares the Tabs identity namespace.');
  }
  return (
    <section
      id={panelDomId(namespace, id)}
      role="tabpanel"
      aria-labelledby={tabDomId(namespace, id)}
      hidden={!active}
      tabIndex={0}
      className={`focus:outline-none ${className}`.trim()}
    >
      {children}
    </section>
  );
}
